import { Injectable, signal, computed } from '@angular/core';
import { CartItem } from '../models/cart.model';
import { PosCustomer } from '../models/pos-catalog.model';
import { toCents, fromCents, applyDiscountPct, sumCents } from '../../shared/utils/money.utils';

/**
 * Total d'une ligne en centimes.
 *
 * Privilégie le lineTotalCents fourni par le backend : pour un BOGO ou un
 * packaging à multiplicateur non-diviseur, le prix unitaire est une moyenne
 * arrondie au centime, et le re-multiplier par la quantité dérive
 * (3 pour 2 à 10.00 → 6.67 × 3 = 20.01 au lieu de 20.00).
 *
 * Repli sur price × qty uniquement quand la valeur exacte n'est pas
 * disponible : avant la première réponse de /pos/calculate, ou sur un panier
 * restauré d'une version antérieure.
 */
export function lineTotalCentsOf(item: CartItem): number {
    return item.lineTotalCents ?? toCents(item.price) * item.qty;
}

/**
 * Etat du panier et totaux qui en dérivent.
 *
 * Sorti de `CartService`, au-delà de la limite de 500 lignes : les
 * collaborateurs (encaissement, restauration, ajouts offerts) lisent et
 * écrivent ces signaux sans dépendre de la façade, ce qui évite tout cycle.
 * `CartService` les republie à l'identique : les composants continuent de les
 * lire là où ils les lisaient.
 */
@Injectable({
    providedIn: 'root'
})
export class CartStateService {
    readonly items = signal<CartItem[]>([]);
    readonly discount = signal<number>(0); // Global percentage 0-100
    readonly selectedCustomer = signal<PosCustomer | null>(null);

    readonly count = computed(() => this.items().length);

    /**
     * Sous-total en centimes (integer) — somme de (priceCents * qty) par article.
     * Les prix dans CartItem.price sont en décimal (contrat backend), convertis ici.
     */
    readonly subTotalCents = computed(() =>
        sumCents(this.items().map(item => lineTotalCentsOf(item)))
    );

    /** Sous-total en décimal pour affichage dans les templates. */
    readonly subTotal = computed(() => fromCents(this.subTotalCents()));

    /** Montant de la remise globale en centimes. */
    readonly discountAmountCents = computed(() => {
        const pct = this.discount();
        if (pct <= 0) return 0;
        return this.subTotalCents() - applyDiscountPct(this.subTotalCents(), pct);
    });

    /** Montant de la remise en décimal pour affichage. */
    readonly discountAmount = computed(() => fromCents(this.discountAmountCents()));

    /** Total taxable en centimes après remise. */
    readonly taxableTotalCents = computed(() => Math.max(0, this.subTotalCents() - this.discountAmountCents()));

    /** Total taxable en décimal pour affichage. */
    readonly taxableTotal = computed(() => fromCents(this.taxableTotalCents()));

    /** Taxes calculées par le backend (BFF) — montants en centimes. */
    readonly taxes = signal<{ name: string; rate: number; amount: number }[]>([]);

    /** Total taxes en centimes. */
    readonly totalTaxesCents = computed(() => sumCents(this.taxes().map(t => toCents(t.amount))));

    /** Total taxes en décimal pour affichage. */
    readonly totalTaxes = computed(() => fromCents(this.totalTaxesCents()));

    /** Total final en centimes (taxable + taxes). */
    readonly totalCents = computed(() => this.taxableTotalCents() + this.totalTaxesCents());

    /** Total final en décimal pour affichage dans les templates. */
    readonly total = computed(() => fromCents(this.totalCents()));
}
